import React from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ConfirmProvider } from "@/components/ConfirmDialog";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Legal from "@/pages/Legal";
import Profile from "@/pages/Profile";
import ClientDashboard from "@/pages/ClientDashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import IntakeWizard from "@/pages/IntakeWizard";
import ScriptView from "@/pages/ScriptView";
import AdminOverview from "@/pages/AdminOverview";
import AdminClients from "@/pages/AdminClients";
import AdminCodes from "@/pages/AdminCodes";
import AdminTags from "@/pages/AdminTags";
import AdminPromptConfig from "@/pages/AdminPromptConfig";

const RootRedirect = () => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Landing />;
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
};

function App() {
    return (
        <AuthProvider>
            <ConfirmProvider>
                <BrowserRouter>
                <Header />
                <div className="min-h-[calc(100vh-8rem)]">
                <Routes>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute role="client">
                                <Profile />
                            </ProtectedRoute>
                        }
                    />

                    {/* Client routes */}
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute role="client">
                                <ClientDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/projects/:id"
                        element={
                            <ProtectedRoute role="client">
                                <ProjectDetail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/projects/:id/intake"
                        element={
                            <ProtectedRoute role="client">
                                <IntakeWizard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/scripts/:id"
                        element={
                            <ProtectedRoute role="client">
                                <ScriptView />
                            </ProtectedRoute>
                        }
                    />

                    {/* Admin routes */}
                    <Route
                        path="/admin"
                        element={
                            <ProtectedRoute role="admin">
                                <AdminOverview />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/clients"
                        element={
                            <ProtectedRoute role="admin">
                                <AdminClients />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/codes"
                        element={
                            <ProtectedRoute role="admin">
                                <AdminCodes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/tags"
                        element={
                            <ProtectedRoute role="admin">
                                <AdminTags />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/prompt"
                        element={
                            <ProtectedRoute role="admin">
                                <AdminPromptConfig />
                            </ProtectedRoute>
                        }
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </div>
                <Footer />
                <Toaster
                    position="top-right"
                    toastOptions={{
                        style: {
                            background: "#000000",
                            color: "#ffffff",
                            border: "2px solid #DF1283",
                            borderRadius: "0px",
                            fontFamily: "'Archivo', system-ui, sans-serif",
                            fontWeight: 600,
                        },
                    }}
                />
            </BrowserRouter>
            </ConfirmProvider>
        </AuthProvider>
    );
}

export default App;
